import { Flashlight, FlashlightOff, Focus } from 'lucide-react';
import { useEffect, useRef, useState, useCallback, forwardRef, useImperativeHandle } from 'react';
import { FrogMascot } from '@/components/FrogMascot';
import { cn } from '@/lib/utils';
import {
  startCamera,
  stopCamera,
  captureFrame,
  captureMultipleFrames,
  selectBestFrame,
  assessCaptureQuality,
  checkTorchSupport,
  setTorch,
  triggerRefocus,
  type QualityAssessment
} from '@/services/camera';
import {
  MotionDetectionController,
  type MotionState,
  type MotionDetectionOptions
} from '@/services/motionDetection';
import {
  VideoCaptureController,
  type VideoCaptureOptions,
  type VideoCaptureResult
} from '@/services/videoCapture';
import { useSoulBondsStore } from '@/stores/soulBondsStore';

// Re-export types for consumers
export type { QualityAssessment } from '@/services/camera';
export type { VideoCaptureOptions, VideoCaptureResult } from '@/services/videoCapture';
export type { MotionState, MotionDetectionOptions } from '@/services/motionDetection';

const SERVICE_TAG = '[CAMERA]';

/**
 * Options for multi-frame capture
 */
export interface MultiCaptureOptions {
  /** Number of frames to capture (default: 3) */
  frameCount?: number;
  /** Interval between frames in ms (default: 200) */
  intervalMs?: number;
}

/**
 * Result of multi-frame capture
 */
export interface MultiCaptureResult {
  /** Best frame selected by quality score */
  bestFrame: string;
  /** All captured frames */
  allFrames: string[];
  /** Quality scores for each frame */
  qualityScores: number[];
}

export interface CameraViewHandle {
  /** Capture a single frame */
  capture: () => string | null;
  /** Capture multiple frames and return best + all frames */
  captureMultiple: (options?: MultiCaptureOptions) => Promise<MultiCaptureResult | null>;
  /** Assess the current frame quality without capturing */
  assessQuality: () => QualityAssessment | null;
  /** Start video capture sequence for frame extraction */
  startVideoCapture: (options?: VideoCaptureOptions) => Promise<VideoCaptureResult>;
  /** Stop video capture and return results */
  stopVideoCapture: () => Promise<VideoCaptureResult | null>;
  /** Check if video recording is in progress */
  isRecording: () => boolean;
  /** Start motion detection for auto-capture (TICKET-071) */
  startMotionDetection: (options?: MotionDetectionOptions) => void;
  /** Stop motion detection */
  stopMotionDetection: () => void;
  /** Reset settled state to allow re-triggering */
  resetMotionSettled: () => void;
  /** Get current motion detection state */
  getMotionState: () => MotionState;
  /** Trigger camera refocus (TICKET-077) */
  refocus: () => Promise<boolean>;
  /** Toggle torch/flashlight on or off */
  toggleTorch: () => Promise<void>;
  /** Get the underlying MediaStream for external recorders (TICKET-281c). Null if camera not ready. */
  getMediaStream: () => MediaStream | null;
  /** Get the underlying <video> element for external motion detectors (TICKET-290). Null if not mounted. */
  getVideoElement: () => HTMLVideoElement | null;
}

interface CameraViewProps {
  className?: string;
  onError?: (error: Error) => void;
  onReady?: () => void;
  /** Callback when torch state changes (supported/enabled) */
  onTorchStateChange?: (state: TorchState) => void;
  /** Callback when refocus state changes */
  onRefocusStateChange?: (state: RefocusState) => void;
}

export interface TorchButtonProps {
  enabled: boolean;
  onToggle: () => void;
}

/**
 * Torch/flashlight toggle button for camera overlay.
 * Render inside CameraTopBar's right slot.
 */
export function TorchButton({ enabled, onToggle }: TorchButtonProps) {
  return (
    <button
      onClick={onToggle}
      className="p-2 rounded-md bg-black/40 hover:bg-black/60 text-white/80 hover:text-white transition-colors"
      aria-label={enabled ? 'Turn off flashlight' : 'Turn on flashlight'}
    >
      {enabled ? (
        <Flashlight className="h-5 w-5 text-yellow-400" />
      ) : (
        <FlashlightOff className="h-5 w-5" />
      )}
    </button>
  );
}

export interface RefocusButtonProps {
  onRefocus: () => void;
  isRefocusing: boolean;
}

/**
 * Refocus button for camera overlay (TICKET-077).
 * Render inside CameraTopBar's left slot.
 */
export function RefocusButton({ onRefocus, isRefocusing }: RefocusButtonProps) {
  return (
    <button
      onClick={onRefocus}
      disabled={isRefocusing}
      className={cn(
        'p-2 rounded-md bg-black/40 hover:bg-black/60 text-white/80 hover:text-white transition-colors',
        isRefocusing && 'opacity-50 cursor-not-allowed'
      )}
      aria-label="Tap to refocus camera"
    >
      <Focus className={cn('h-5 w-5', isRefocusing && 'animate-pulse')} />
    </button>
  );
}

/** Torch state pushed from CameraView to parent */
export interface TorchState {
  supported: boolean;
  enabled: boolean;
}

/** Refocus state pushed from CameraView to parent */
export interface RefocusState {
  isRefocusing: boolean;
}

/**
 * Camera preview component with capture capability
 */
export const CameraView = forwardRef<CameraViewHandle, CameraViewProps>(
  ({ className, onError, onReady, onTorchStateChange, onRefocusStateChange }, ref) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const videoCaptureRef = useRef<VideoCaptureController>(new VideoCaptureController());
    const motionDetectorRef = useRef<MotionDetectionController>(new MotionDetectionController());
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [torchSupported, setTorchSupported] = useState(false);
    const [torchEnabled, setTorchEnabled] = useState(false);
    const [isRefocusing, setIsRefocusing] = useState(false);

    const { appSettings, updateSettings } = useSoulBondsStore();

    useImperativeHandle(ref, () => ({
      capture: () => {
        if (!videoRef.current || !streamRef.current) {
          return null;
        }
        return captureFrame(videoRef.current);
      },
      captureMultiple: async (options?: MultiCaptureOptions): Promise<MultiCaptureResult | null> => {
        if (!videoRef.current || !streamRef.current) {
          console.warn(`${SERVICE_TAG} Cannot capture - camera not ready`);
          return null;
        }

        try {
          const frames = await captureMultipleFrames(videoRef.current, {
            frameCount: options?.frameCount,
            intervalMs: options?.intervalMs
          });

          if (frames.length === 0) {
            console.warn(`${SERVICE_TAG} Multi-frame capture returned no frames`);
            return null;
          }

          const bestFrame = selectBestFrame(frames);

          return {
            bestFrame: bestFrame.data,
            allFrames: frames.map((f) => f.data),
            qualityScores: frames.map((f) => f.qualityScore)
          };
        } catch (error) {
          console.error(`${SERVICE_TAG} Multi-frame capture failed:`, {
            error: error instanceof Error ? error.message : 'Unknown error'
          });
          return null;
        }
      },
      assessQuality: (): QualityAssessment | null => {
        if (!videoRef.current || !streamRef.current) {
          console.warn(`${SERVICE_TAG} Cannot assess quality - camera not ready`);
          return null;
        }
        return assessCaptureQuality(videoRef.current);
      },
      startVideoCapture: async (options?: VideoCaptureOptions): Promise<VideoCaptureResult> => {
        if (!videoRef.current || !streamRef.current) {
          console.warn(`${SERVICE_TAG} Cannot start video capture - camera not ready`);
          return {
            allFrames: [],
            settledFrames: [],
            uniqueFrames: [],
            durationMs: 0,
            startTime: Date.now(),
            endTime: Date.now()
          };
        }
        return videoCaptureRef.current.startCapture(videoRef.current, options);
      },
      stopVideoCapture: async (): Promise<VideoCaptureResult | null> => {
        return videoCaptureRef.current.stopCapture();
      },
      isRecording: (): boolean => {
        return videoCaptureRef.current.isRecording();
      },
      // TICKET-071: Motion detection for auto-capture
      startMotionDetection: (options?: MotionDetectionOptions): void => {
        if (!videoRef.current || !streamRef.current) {
          console.warn(`${SERVICE_TAG} Cannot start motion detection - camera not ready`);
          return;
        }
        motionDetectorRef.current.start(videoRef.current, options);
      },
      stopMotionDetection: (): void => {
        motionDetectorRef.current.stop();
      },
      resetMotionSettled: (): void => {
        motionDetectorRef.current.resetSettled();
      },
      getMotionState: (): MotionState => {
        return motionDetectorRef.current.getState();
      },
      // TICKET-077: Refocus capability
      refocus: async (): Promise<boolean> => {
        if (!videoRef.current || !streamRef.current || isRefocusing) {
          if (!videoRef.current || !streamRef.current) {
            console.warn(`${SERVICE_TAG} Cannot refocus - camera not ready`);
          }
          return false;
        }
        setIsRefocusing(true);
        onRefocusStateChange?.({ isRefocusing: true });
        const result = await triggerRefocus(streamRef.current, videoRef.current);
        setTimeout(() => {
          setIsRefocusing(false);
          onRefocusStateChange?.({ isRefocusing: false });
        }, 500);
        return result;
      },
      toggleTorch: async (): Promise<void> => {
        if (!streamRef.current) return;
        const newState = !torchEnabled;
        const success = await setTorch(streamRef.current, newState);
        if (success) {
          setTorchEnabled(newState);
          updateSettings({ torchEnabled: newState });
          onTorchStateChange?.({ supported: torchSupported, enabled: newState });
        }
      },
      getMediaStream: (): MediaStream | null => streamRef.current,
      getVideoElement: (): HTMLVideoElement | null => videoRef.current
    }));

    const initCamera = useCallback(async () => {
      if (!videoRef.current) return;

      setIsLoading(true);
      setError(null);

      try {
        const stream = await startCamera(videoRef.current, {
          deviceId: appSettings.preferredCamera
        });
        streamRef.current = stream;

        // Store the active camera deviceId for future sessions
        const videoTrack = stream.getVideoTracks()[0];
        if (videoTrack) {
          const settings = videoTrack.getSettings();
          if (settings.deviceId && settings.deviceId !== appSettings.preferredCamera) {
            console.info(`${SERVICE_TAG} Saving preferred camera`, { deviceId: settings.deviceId });
            updateSettings({ preferredCamera: settings.deviceId });
          }
        }

        // Check torch support and restore preference
        const supported = await checkTorchSupport(stream);
        setTorchSupported(supported);

        let torchOn = false;
        if (supported && appSettings.torchEnabled) {
          const success = await setTorch(stream, true);
          if (success) {
            setTorchEnabled(true);
            torchOn = true;
          }
        }
        onTorchStateChange?.({ supported, enabled: torchOn });

        setIsLoading(false);
        onReady?.();
      } catch (err) {
        const cameraError = err instanceof Error ? err : new Error('Camera access denied');

        // If preferred camera failed, try without it
        if (appSettings.preferredCamera) {
          console.warn(`${SERVICE_TAG} Preferred camera not available, trying default`, {
            preferredCamera: appSettings.preferredCamera
          });
          try {
            const stream = await startCamera(videoRef.current!);
            streamRef.current = stream;

            // Check torch support for fallback camera
            const supported = await checkTorchSupport(stream);
            setTorchSupported(supported);

            let torchOn = false;
            if (supported && appSettings.torchEnabled) {
              const success = await setTorch(stream, true);
              if (success) {
                setTorchEnabled(true);
                torchOn = true;
              }
            }
            onTorchStateChange?.({ supported, enabled: torchOn });

            setIsLoading(false);
            onReady?.();
            return;
          } catch {
            // Fall through to error handling
          }
        }

        setError(getCameraErrorMessage(cameraError));
        setIsLoading(false);
        onError?.(cameraError);
      }
    }, [onError, onReady, onTorchStateChange, appSettings.preferredCamera, appSettings.torchEnabled, updateSettings]);

    useEffect(() => {
      initCamera();

      return () => {
        // Stop motion detection on unmount
        motionDetectorRef.current.stop();

        if (streamRef.current) {
          stopCamera(streamRef.current);
          streamRef.current = null;
        }
      };
    }, [initCamera]);

    if (error) {
      return (
        <div className={cn('camera-frame h-full', className)}>
          <div className="camera-glass h-full flex flex-col items-center justify-center p-6 text-center min-h-[200px]">
            <FrogMascot variant="error" size="md" className="mb-4 opacity-70" />
            <p className="mb-2 font-medium text-destructive">Camera unavailable</p>
            <p className="text-sm text-muted-foreground">{error}</p>
            <button
              onClick={initCamera}
              className="mt-4 text-sm text-primary underline hover:text-glow-subtle transition-all"
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className={cn('camera-frame h-full', className)}>
        <div className="camera-glass relative overflow-hidden h-full">
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-secondary/80 z-10">
              <div className="text-center">
                <FrogMascot variant="loading" size="xl" className="mb-2 mx-auto" />
                <p className="text-sm text-muted-foreground">Starting camera...</p>
              </div>
            </div>
          )}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="h-full w-full object-cover"
          />
        </div>
      </div>
    );
  }
);

CameraView.displayName = 'CameraView';

/**
 * Get user-friendly error message for camera errors
 */
function getCameraErrorMessage(error: Error): string {
  const message = error.message.toLowerCase();

  if (message.includes('permission') || message.includes('denied')) {
    return 'Please allow camera access in your browser settings';
  }

  if (message.includes('not found') || message.includes('no video')) {
    return 'No camera found on this device';
  }

  if (message.includes('insecure') || message.includes('https')) {
    return 'Camera requires a secure connection (HTTPS)';
  }

  return 'Could not access camera. Please check your settings.';
}
